import { CssBaseline, ThemeProvider } from '@mui/material'
import theme from './theme'
import AlertDialog from './components/dialogs/AlertDialog'
import FullPageLoading from './components/FullPageLoading'
import OpenDamageRegistrationPageFab from './components/OpenDamageRegistrationPageFab'
import AddVehicleIdDialog from './components/dialogs/AddVehicleIdDialog'
import KmInputDialog from './components/dialogs/KmInputDialog'
import CustomSnackbar from './components/CustomSnackbar'
import MainPage from './pages/Main'
import useAuthToken from './hooks/useAuthToken'
import 'react-html5-camera-photo/build/css/index.css'
import './App.css'
import { DHL_API } from './api'
import { useQuery } from '@tanstack/react-query'
import { useAppStore } from './store/useAppStore'
import { QueryKey } from './constants'

interface Depot {
  depotNumber: string
  depotCode: string
}

function App() {
  // Take auth token from URL, store in localStorage and delete it from URL for security reasons;
  useAuthToken()
  const depotCode = useAppStore((state) => state.depotCode)
  const setDepotCode = useAppStore((state) => state.setDepotCode)

  useQuery({
    queryKey: [QueryKey.Depots],
    queryFn: () => DHL_API.get<Depot[]>(`api/fleet/storegear/depots`),
    onSuccess: (data) => {
      const urlParams = new URLSearchParams(window.location.search)
      // Initially we have depotNumber in URL, so we need to replace it with depotCode
      const depotNumber = urlParams.get('depotCode')
      // This is a case when the window is opened with correct depotCode it's a string value
      if (depotNumber && isNaN(+depotNumber)) {
        setDepotCode(depotNumber)
      }

      if (!depotCode && depotNumber && !isNaN(+depotNumber)) {
        const depot = data.data.find(
          (depot: Depot) => depot.depotNumber === depotNumber
        )
        urlParams.forEach((param) => {
          if (param === depotNumber && depot) {
            urlParams.set('depotCode', depot.depotCode)
            window.history.replaceState(
              null,
              window.location.search,
              '?' + urlParams.toString()
            )
            setDepotCode(depot.depotCode)
          }
        })
      }
      return data
    },
    onError: () => {
      console.error('Failed to fetch depots')
      // Null is used to indicate that depotCode is not set
      // This will trigger to get depots again and set depotCode by using depotNumber from URL
      // This is needed to avoid infinite loop of fetching depots
      setDepotCode('')
    },
    enabled: depotCode === null,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
  })

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <MainPage />
      <OpenDamageRegistrationPageFab />
      <KmInputDialog />
      <AddVehicleIdDialog />
      <AlertDialog />
      <CustomSnackbar />
      <FullPageLoading />
    </ThemeProvider>
  )
}

export default App
